import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/Toast';

const STEP_INFO = {
  step1:            { num: 1, label: '업로드',   color: '#6B7280', group: 'prep' },
  step2_ready:      { num: 2, label: '대본추출 대기', color: '#6B7280', group: 'prep' },
  step2_processing: { num: 2, label: '대본추출 중', color: '#2196F3', group: 'edit' },
  step3:            { num: 3, label: '대사 편집', color: '#2196F3', group: 'edit' },
  step4:            { num: 4, label: '편집 확정', color: '#2196F3', group: 'edit' },
  step5_processing: { num: 5, label: '1차 생성 중', color: '#FF9800', group: 'proc' },
  step5:            { num: 5, label: '1차 완료', color: '#FF9800', group: 'proc' },
  step6:            { num: 6, label: '1차 확인', color: '#FF9800', group: 'proc' },
  step7_ready:      { num: 7, label: '2차 대본 대기', color: '#4CAF50', group: 'done' },
  step7_processing: { num: 7, label: '2차 대본 생성 중', color: '#4CAF50', group: 'done' },
  step7:            { num: 7, label: '2차 대본',  color: '#4CAF50', group: 'done' },
  step7_editing:    { num: 7, label: '2차 편집 중', color: '#4CAF50', group: 'done' },
  step8_processing: { num: 8, label: '최종 생성 중', color: '#4CAF50', group: 'done' },
  step8:            { num: 8, label: '최종 완성', color: '#4CAF50', group: 'done' },
  completed:        { num: 8, label: '완료',     color: '#4CAF50', group: 'done' },
};

function formatDuration(seconds) {
  if (!seconds) return '-';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}분 ${s}초`;
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export default function ProjectList() {
  const { api } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadProjects = useCallback(async () => {
    try {
      const { data } = await api.get('/v3/project/list');
      setProjects(data.projects || []);
    } catch (err) {
      addToast('프로젝트 목록을 불러오지 못했습니다', 'error');
    } finally {
      setLoading(false);
    }
  }, [api, addToast]);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('이 프로젝트를 삭제하시겠습니까?\n모든 영상 파일이 함께 삭제됩니다.')) return;
    try {
      await api.delete(`/v3/project/${id}`);
      addToast('프로젝트가 삭제되었습니다', 'success');
      loadProjects();
    } catch (err) {
      addToast('삭제 실패', 'error');
    }
  };

  return (
    <div>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>프로젝트 목록</h1>
          <p style={styles.subtitle}>영상 자동편집 프로젝트를 관리합니다</p>
        </div>
        <button style={styles.createBtn} onClick={() => navigate('/v3/project/new')}>
          + 새 프로젝트
        </button>
      </div>

      {loading ? (
        <div style={styles.loadingWrap}>
          <div style={styles.spinner} />
          <span style={{ color: '#8B949E' }}>로딩 중...</span>
        </div>
      ) : projects.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={{ fontSize: 56, marginBottom: 16, opacity: 0.6 }}>🎬</div>
          <p style={{ color: '#E6EDF3', fontSize: 18, fontWeight: 600, margin: '0 0 4px' }}>아직 프로젝트가 없습니다</p>
          <p style={{ color: '#6B7280', fontSize: 14, margin: '0 0 24px' }}>새 프로젝트를 만들어 영상 편집을 시작하세요</p>
          <button style={styles.createBtn} onClick={() => navigate('/v3/project/new')}>
            첫 프로젝트 만들기
          </button>
        </div>
      ) : (
        <div style={styles.grid}>
          {projects.map(p => {
            const info = STEP_INFO[p.status] || STEP_INFO.step1;
            const progress = (info.num / 8) * 100;

            return (
              <div
                key={p._id}
                style={styles.card}
                onClick={() => navigate(`/v3/project/${p._id}`)}
                onMouseEnter={e => { e.currentTarget.style.borderColor = info.color; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#2A3441'; e.currentTarget.style.transform = 'none'; }}
              >
                {/* 헤더: 제목 + 날짜 */}
                <div style={styles.cardHeader}>
                  <h3 style={styles.cardTitle}>
                    <span style={{ marginRight: 8, fontSize: 16 }}>🎬</span>
                    {p.name}
                  </h3>
                  <span style={styles.cardDate}>{formatDate(p.createdAt)}</span>
                </div>

                {/* 진행률 바 */}
                <div style={styles.progressWrap}>
                  <div style={styles.progressTrack}>
                    <div style={{ ...styles.progressFill, width: `${progress}%`, backgroundColor: info.color }} />
                  </div>
                  <span style={{ ...styles.stepBadge, color: info.color, borderColor: info.color + '55', backgroundColor: info.color + '15' }}>
                    STEP {info.num}
                  </span>
                </div>

                {/* 상태 라벨 */}
                <div style={{ ...styles.statusLabel, color: info.color }}>
                  {info.label}
                </div>

                {/* 메타 정보 */}
                {p.originalVideo?.duration > 0 && (
                  <div style={styles.metaRow}>
                    <span style={styles.metaLabel}>원본</span>
                    <span style={styles.metaValue}>{formatDuration(p.originalVideo.duration)}</span>
                  </div>
                )}

                {/* 하단: 버튼 */}
                <div style={styles.cardFooter}>
                  <button
                    style={styles.continueBtn}
                    onClick={(e) => { e.stopPropagation(); navigate(`/v3/project/${p._id}`); }}
                  >
                    계속하기 →
                  </button>
                  <button
                    style={styles.deleteBtn}
                    onClick={(e) => handleDelete(e, p._id)}
                    title="삭제"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const styles = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 32,
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    color: '#E6EDF3',
    margin: 0,
  },
  subtitle: {
    color: '#6B7280',
    fontSize: 14,
    marginTop: 4,
  },
  createBtn: {
    padding: '10px 24px',
    backgroundColor: '#4CAF50',
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'Noto Sans KR', sans-serif",
    transition: 'background-color 0.15s',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
    gap: 16,
  },
  card: {
    backgroundColor: '#1E2732',
    border: '1px solid #2A3441',
    borderRadius: 16,
    padding: 20,
    cursor: 'pointer',
    transition: 'border-color 0.2s, transform 0.2s, box-shadow 0.2s',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  cardTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: '#E6EDF3',
    margin: 0,
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center',
  },
  cardDate: {
    fontSize: 11,
    color: '#6B7280',
    flexShrink: 0,
    marginLeft: 12,
  },
  progressWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 8,
  },
  progressTrack: {
    flex: 1,
    height: 6,
    backgroundColor: '#111827',
    borderRadius: 3,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 3,
    transition: 'width 0.3s',
  },
  stepBadge: {
    fontSize: 11,
    fontWeight: 700,
    padding: '2px 8px',
    borderRadius: 4,
    border: '1px solid',
    flexShrink: 0,
  },
  statusLabel: {
    fontSize: 12,
    fontWeight: 500,
    marginBottom: 12,
  },
  metaRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 13,
    marginBottom: 16,
  },
  metaLabel: {
    color: '#6B7280',
  },
  metaValue: {
    color: '#C9D1D9',
  },
  cardFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTop: '1px solid #2A3441',
    paddingTop: 12,
    marginTop: 4,
  },
  continueBtn: {
    padding: '6px 16px',
    backgroundColor: '#2A3441',
    border: 'none',
    borderRadius: 8,
    color: '#E6EDF3',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: "'Noto Sans KR', sans-serif",
    transition: 'background-color 0.15s',
  },
  deleteBtn: {
    background: 'none',
    border: 'none',
    fontSize: 16,
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: 6,
    opacity: 0.5,
    transition: 'opacity 0.15s',
  },
  loadingWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 80,
    gap: 12,
  },
  spinner: {
    width: 36,
    height: 36,
    border: '3px solid #2A3441',
    borderTop: '3px solid #4CAF50',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 100,
  },
};
